import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const emptyReview = { name: "", country: "", quote: "", image: "", rating: "5" };

export const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyReview);

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addReview = async () => {
    try {
      await api.post("/admin/reviews", {
        name: form.name.trim(), country: form.country.trim(), quote: form.quote.trim(),
        image: form.image.trim() || null, rating: Number(form.rating),
      });
      toast.success("Review added");
      setOpen(false);
      setForm(emptyReview);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) {
    return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-red" /></div>;
  }

  return (
    <div data-testid="admin-reviews-panel">
      <div className="mb-4 flex justify-between items-center">
        <div className="text-brand-navy font-semibold">{reviews.length} reviews on the landing page</div>
        <Button data-testid="new-review-btn" onClick={() => setOpen(true)} className="rounded-full bg-brand-red hover:bg-brand-red-hover">
          <Plus className="w-4 h-4 mr-1" /> Add review
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-admin-row-${r.id}`} className="rounded-2xl bg-white border border-brand-navy/10 p-5">
            <div className="flex items-start gap-3">
              {r.image ? (
                <img src={r.image} alt={r.name} className="w-12 h-12 rounded-full object-cover object-top ring-2 ring-brand-red/20 shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-brand-red/10 text-brand-red grid place-items-center font-heading font-black shrink-0">
                  {(r.name || "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-heading font-bold text-brand-navy truncate">{r.name} · {r.country}</div>
                  <button
                    data-testid={`delete-review-${r.id}`}
                    onClick={() => deleteReview(r.id)}
                    className="text-brand-red/70 hover:text-brand-red shrink-0"
                    aria-label="Delete review"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-0.5 text-brand-gold mt-1">
                  {[...Array(r.rating || 5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <p className="text-sm text-brand-navy/70 mt-2 line-clamp-3">&quot;{r.quote}&quot;</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-brand-navy">New review</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Student name</Label><Input data-testid="review-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Country</Label><Input data-testid="review-form-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Canada" /></div>
            </div>
            <div><Label>Review text</Label><Textarea data-testid="review-form-quote" rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} /></div>
            <div><Label>Photo URL (optional)</Label><Input data-testid="review-form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." /></div>
            <div>
              <Label>Rating</Label>
              <Select value={form.rating} onValueChange={(v) => setForm({ ...form, rating: v })}>
                <SelectTrigger data-testid="review-form-rating" className="rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["5", "4", "3", "2", "1"].map((n) => <SelectItem key={n} value={n}>{n} stars</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="review-form-save"
              onClick={addReview}
              disabled={!form.name.trim() || !form.country.trim() || !form.quote.trim()}
              className="w-full rounded-full bg-brand-red hover:bg-brand-red-hover"
            >
              Publish review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
